import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { notifyUser } from "components/shared-components/AdvancareNotification";
import SrdService from "services/srdService";
import { removeRecordForSrd, updateDrawHistoryForSrd } from "utils/SrdUtils";

export const initialState = {
  list: [],
  loading: false,
  drawHistoryForSrd: [],
  drawHistoryForSrdLoading: false,
  creating: false,
  updating: false,
  errorMessages: [],
  showMessage: false,
  tubetrackingreportlist: [],
  tubeTrackingReportRequestId: null,
};

export const srdTableData = createAsyncThunk(
  "/srdTableData",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SrdService.srdTableDatas(data);
      var code = response.code;
      return {
        code: code,
        datas: response.data,
      };
    } catch (err) {
      let code = err.response?.data.code;
      let response = err.response;
      return rejectWithValue({
        code: code,
        errorMessages: response?.data?.message || "Error",
      });
    }
  }
);

export const phlebotomyDrawHistoryForSrd = createAsyncThunk(
  "/phlebotomyDrawHistoryForSrd",
  async (sampleId, { rejectWithValue }) => {
    try {
      const response = await SrdService.drawHistoryForSrd(sampleId);
      var code = response.code;
      return {
        code: code,
        datas: response.data,
      };
    } catch (err) {
      let code = err.response?.data.code;
      let response = err.response;
      return rejectWithValue({
        code: code,
        errorMessages: response?.data?.message || "Error",
      });
    }
  }
);

export const srdReceive = createAsyncThunk(
  "/srdReceive",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SrdService.srdReceive(data);
      var code = response.code;
      return {
        code: code,
        datas: response.data[0],
      };
    } catch (err) {
      let code = err.response?.data.code;
      let response = err.response;
      return rejectWithValue({
        code: code,
        errorMessages: response?.data?.message || "Error",
      });
    }
  }
);

export const srdReject = createAsyncThunk(
  "/srdReject",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SrdService.srdReject(data);
      var code = response.code;
      return {
        code: code,
        data: response.data,
      };
    } catch (err) {
      let code = err.response?.data.code;
      let response = err.response;
      return rejectWithValue({
        code: code,
        errorMessages: response?.data?.message || "Error",
      });
    }
  }
);
export const srdRetain = createAsyncThunk(
  "/srdRetain",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SrdService.srdRetain(data);
      const code = response.code;
      return {
        code: code,
        data: response.data,
      };
    } catch (err) {
      let code = err.response?.data.code;
      let response = err.response;
      return rejectWithValue({
        code: code,
        errorMessages: response?.data?.message || "Error",
      });
    }
  }
);

export const srdDispose = createAsyncThunk(
  "/srdDispose",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SrdService.srdDispose(data);
      const code = response.code;
      return {
        code: code,
        data: response.data,
      };
    } catch (err) {
      return rejectWithValue({ error: "Error Disposing" });
    }
  }
);

export const srdReport = createAsyncThunk(
  "/srdReport",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SrdService.srdReport(data);
      const code = response.code;
      return {
        code: code,
        data: response.data,
      };
    } catch (err) {
      return rejectWithValue({ error: "Error on SRD Report" });
    }
  }
);

export const srdTubeTrackingReport = createAsyncThunk(
  "/srdRTubeTrackingeport",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SrdService.srdTubeTrackingReport(data);
      const code = response.code;
      return {
        code: code,
        data: response.data,
      };
    } catch (err) {
      return rejectWithValue({ error: "Error on SRD Tube Tracking Report" });
    }
  }
);

export const srdSlice = createSlice({
  name: "srd",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = !state.loading;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(srdTableData.pending, (state) => {
        state.loading = true;
      })
      .addCase(srdTableData.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.datas;
      })
      .addCase(srdTableData.rejected, (state, action) => {
        state.loading = false;
        notifyUser("error", "topRight", action.payload.errorMessages);
      })
      .addCase(phlebotomyDrawHistoryForSrd.pending, (state) => {
        state.drawHistoryForSrdLoading = true;
      })
      .addCase(phlebotomyDrawHistoryForSrd.fulfilled, (state, action) => {
        state.drawHistoryForSrdLoading = false;
        state.drawHistoryForSrd = action.payload.datas;
      })
      .addCase(phlebotomyDrawHistoryForSrd.rejected, (state, action) => {
        notifyUser("info", "topRight", action.payload.errorMessages);
        state.drawHistoryForSrdLoading = false;
        state.drawHistoryForSrd = [];
      })
      .addCase(srdReceive.pending, (state) => {
        state.loading = true;
      })
      .addCase(srdReceive.fulfilled, (state, action) => {
        state.loading = false;
        state.drawHistoryForSrd = updateDrawHistoryForSrd(
          state.drawHistoryForSrd,
          action.payload.datas
        );
        notifyUser("success", "topRight", "Specimen received successfully!");
      })
      .addCase(srdReceive.rejected, (state, action) => {
        notifyUser("error", "topRight", "Error receiving the specimen");
        state.loading = false;
      })
      .addCase(srdReject.pending, (state) => {
        state.loading = true;
      })
      .addCase(srdReject.fulfilled, (state, action) => {
        state.loading = false;
        state.drawHistoryForSrd = removeRecordForSrd(
          state.drawHistoryForSrd,
          action.payload.data
        );
        notifyUser("success", "topRight", "Specimen rejected successfully!");
      })
      .addCase(srdReject.rejected, (state, action) => {
        notifyUser("error", "topRight", "Error rejecting the specimen");
        state.loading = false;
      })
      .addCase(srdRetain.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(srdRetain.fulfilled, (state, action) => {
        state.loading = false;
        state.drawHistoryForSrd = updateDrawHistoryForSrd(
          state.drawHistoryForSrd,
          action.payload.data
        );
      })
      .addCase(srdRetain.rejected, (state, action) => {
        notifyUser("error", "topRight", action.payload.errorMessages);
        state.loading = false;
      })
      .addCase(srdDispose.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(srdDispose.fulfilled, (state, action) => {
        state.loading = false;
        state.drawHistoryForSrd = removeRecordForSrd(
          state.drawHistoryForSrd,
          action.payload.data
        );
      })
      .addCase(srdDispose.rejected, (state, action) => {
        state.loading = false;
        notifyUser("error", "topRight", "Failed to reject");
      })
      .addCase(srdReport.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(srdReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reportlist = action.payload.data || [];
        // state.reportlist = Array.isArray(action.payload.data)
        // ? action.payload.data
        // : [];
        state.error = null;
      })
      .addCase(srdReport.rejected, (state, action) => {
        state.loading = false;
        console.log("-->");
        console.log(action);
        notifyUser("error", "topRight", "Failed to fetch report");
      })

      .addCase(srdTubeTrackingReport.pending, (state, action) => {
        state.loading = true;
        state.tubeTrackingReportRequestId = action.meta.requestId;
      })
      .addCase(srdTubeTrackingReport.fulfilled, (state, action) => {
        state.loading = false;
        // Ignore stale responses from an older, since-superseded request.
        if (action.meta.requestId !== state.tubeTrackingReportRequestId) return;
        state.tubetrackingreportlist = action.payload.data || [];
        state.error = null;
      })
      .addCase(srdTubeTrackingReport.rejected, (state, action) => {
        state.loading = false;
        // console.log('-->');
        // console.log(action);
        notifyUser("error", "topRight", "Failed to fetch tube tracking report");
      });
  },
});

export const { showLoading } = srdSlice.actions;

export default srdSlice.reducer;
